import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';

/**
 * A participant's photo, or their initials where there is none to draw.
 *
 * `photoUri` is survey-api's absolute public URL of the image, or `null` (survey-api !84;
 * before it, the column held the store path `photos/<hash>.jpg`, which nothing served). The file
 * sits in a public blob container, so the image is a plain `<img>` — no bearer, no
 * `API_BASE_URL`, nothing through `client.ts`, and no host spelled here: dev and UAT serve it
 * from a storage account, and a CDN may stand in front of it later. The name is the hash of the
 * content, so a new photo is a new URL and the browser may cache any of them for good.
 *
 * The photos are what the migration carried — jpeg and png, maybe gif or webp, any size, not
 * always square, a png sometimes transparent — so the box is fixed, the image is cropped to it
 * (`object-cover`) and drawn over a muted ground a transparent one shows. **The crop keeps the
 * top**: the photos are headshots, taller than wide, and a centred crop took the top of the head
 * off in every circle. An image that fails to load falls back to the initials, as a null does; the
 * failure is the URL's, so a new URL tries again.
 *
 * Beside a name — a row, a chip, a title — it is a circle. Where the picture is the thing read
 * (the hub's Photo fact) it is a `portrait`: a 4:5 frame with a hairline, the headshot's own
 * shape, because a 96 px circle cut a portrait down to a face with no head and no shoulders.
 *
 * **`decorative` wherever the name is drawn beside it** — a list row, a link, a header over the
 * page's `h1`. There the avatar is hidden from assistive technology and the image's `alt` is
 * empty, or every link and heading it sits in would announce the name twice, and the accessible
 * names the suites drive by would change. Standing alone (the hub's Photo fact), it is named.
 *
 * `data-avatar` says which was drawn — `photo` or `initials` — for the suite to read.
 */
export type AvatarSize = 'sm' | 'md' | 'lg' | 'portrait';

const SIZE: Record<AvatarSize, string> = {
  sm: 'size-6 rounded-full text-[0.625rem]',
  md: 'size-9 rounded-full text-xs',
  lg: 'size-12 rounded-full text-sm',
  // A bare `border` draws in currentColor here (CLAUDE.md, Traps).
  portrait: 'aspect-[4/5] w-32 rounded-lg border border-border text-2xl'
};

export function ParticipantAvatar({
  photoUri,
  displayName,
  size = 'md',
  decorative = false,
  className
}: {
  photoUri: Nullable<string>;
  displayName: string;
  size?: AvatarSize;
  decorative?: boolean;
  className?: string;
}) {
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  const photo = photoUri && failed !== photoUri ? photoUri : null;
  const box = cn(
    'inline-flex shrink-0 items-center justify-center overflow-hidden select-none',
    SIZE[size],
    className
  );

  if (photo) {
    return (
      <span data-avatar="photo" aria-hidden={decorative || undefined} className={cn(box, 'bg-muted')}>
        <img
          src={photo}
          alt={decorative ? '' : displayName}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(photo)}
          className="size-full object-cover object-top"
        />
      </span>
    );
  }

  return (
    <span
      data-avatar="initials"
      aria-hidden={decorative || undefined}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : displayName}
      // Sapphire fill, white initials: 6.5:1 — the account menu's avatar.
      className={cn(box, 'bg-[var(--color-sapphire)] font-medium text-white')}
    >
      {initialsOf(displayName)}
    </span>
  );
}

/** The first letter of the name's first and last words — "Jordan Q Rivers" is "JR", one word
 *  gives one letter. Punctuation and spaces are not words; a name with no letter or digit at all
 *  is "?". */
function initialsOf(displayName: string): string {
  const words = displayName
    .split(/\s+/)
    .map((word) => word.replace(/^[^\p{L}\p{N}]+/u, ''))
    .filter(Boolean);
  if (words.length === 0) return '?';
  const first = Array.from(words[0])[0];
  const last = words.length > 1 ? Array.from(words[words.length - 1])[0] : '';
  return `${first}${last}`.toUpperCase();
}
